import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { omit, isEqual } from "lodash";

import AddToLibraryButton from "../../../../../../components/ui/add-to-library-button/AddToLibraryButton";
import {
  ButtonGroup,
  ImagePicker,
  TextInput as Text,
  TextAreaInput as TextArea,
} from "@caelor/ui";
import IconPicker from "../../../../../../components/ui/icon-picker/IconPicker";
import IllustrationPicker from "../../../../../../components/ui/illustration-picker/IllustrationPicker";

import LinkPicker from "../../../../../../components/ui/link-picker/LinkPicker";
import GoBack from "../../components/GoBack";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import { cardsLibraryService } from "@Shared/Service/CardsLibraryService";
import { useCardsStore } from "../../../../../../stores/use-cards-store";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
  TextEditorContainer,
} from "../../styles";
import {
  CARD_DESIGN,
  CARD_TYPE,
  CARDS_PROP,
  DESIGNS_OPTIONS,
  getIconOptions,
  HIDE_COLOR_TILES,
  LIBRARY_SERVICE_ACTION,
  STANDARD_BACKGROUND_COLORS,
} from "./defaults";
import { BOOLEAN_OPTIONS } from "./options";
import { TextEditor } from "@caelor/cards-and-panels-components";
import { useTheme } from "styled-components";
import { ImageFocalPoint } from "@lemoncode/react-image-focal-point";

const CardOptions = () => {
  const theme = useTheme();
  const [isRequestingToLibrary, setIsRequestingToLibrary] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const { cardData, updateCardData, setCard } = useCardsStore((state) => ({
    cardData: state.card,
    updateCardData: state.updateCardData,
    setCard: state.setCard,
  }));

  const { t } = useTranslation();

  const { card, config } = cardData;

  const { libraryCards, setLibraryCards, hasStyleguidePermission } =
    useOpticsStore((state) => ({
      libraryCards: state.libraryCards,
      setLibraryCards: state.setLibraryCards,
      hasStyleguidePermission: state.hasStyleguidePermission,
    }));

  const selectedLibraryCard = libraryCards.find(
    (libraryCard) => libraryCard.cardData.id === card.id
  );

  const handleCardLibraryRequest = async (fnName) => {
    try {
      setIsRequestingToLibrary(true);
      const response = await cardsLibraryService[fnName](
        card.id,
        cardData.config,
        card,
        cardData.tabs || []
      );
      if (response?.data?.cardId) {
        setLibraryCards([
          ...libraryCards,
          { cardData: card, config: cardData.config, tabs: cardData.tabs },
        ]);
      } else {
        setLibraryCards([
          ...libraryCards.map((crd) => {
            if (crd.cardData.id === card.id) {
              return {
                ...crd,
                cardData: card,
                config: cardData.config,
                tabs: cardData.tabs,
              };
            }
            return crd;
          }),
        ]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsRequestingToLibrary(false);
    }
  };

  const isEmoji = typeof card.icon === "object";

  const optionConditions = {
    color:
      config.layout.includes(CARDS_PROP.LABEL) ||
      (config.type === "emoji" && typeof card.icon === "string"),
    background: config.type === "emoji",
  };

  const ICON_OPTIONS = getIconOptions(t);

  return (
    <>
      <GoBack
        onClick={() => setCard(undefined)}
        content={t("selected-card-content")}
      />
      {DESIGNS_OPTIONS.IMAGE.includes(config.design) && (
        <>
          <SettingsOptionContainer>
            <SettingsOptionHeading>
              {config.design === CARD_DESIGN.IMAGE_EIGHT
                ? t("image-top")
                : t("image")}
            </SettingsOptionHeading>
            <ImagePicker
              asParent={false}
              image={card[CARDS_PROP.IMAGE]}
              hideCurrentPage={false}
              open={imagePickerOpen}
              setOpen={setImagePickerOpen}
              onChange={(value) =>
                updateCardData({
                  id: card.id,
                  key: CARDS_PROP.IMAGE,
                  value,
                })
              }
              imageReposition={true}
              ImageRepositionComponent={ImageFocalPoint}
              tabs={["Unsplash", "From page", "Link", "Upload"]}
              sidebarContainerStyles={{}}
              thumbnailContainerStyles={{}}
              libraryImages={[]}
              iconLibraryImages={[]}
              hideInfo={true}
            />
            {DESIGNS_OPTIONS.IMAGE_OVERLAY.includes(config.design) && (
              <OptionContainer>
                <OptionLabel>{t("overlay")}</OptionLabel>
                <OptionContent>
                  <ButtonGroup
                    items={BOOLEAN_OPTIONS}
                    value={card[CARDS_PROP.OVERLAY].isActive}
                    onClick={(isOn) => {
                      const value = {
                        ...card[CARDS_PROP.OVERLAY],
                        isActive: isOn,
                      };

                      updateCardData({
                        id: card.id,
                        key: CARDS_PROP.OVERLAY,
                        value,
                      });
                    }}
                    i18n={t}
                  />
                </OptionContent>
              </OptionContainer>
            )}
          </SettingsOptionContainer>

          {config.design === CARD_DESIGN.IMAGE_EIGHT && (
            <SettingsOptionContainer>
              <SettingsOptionHeading>{t("image-bottom")}</SettingsOptionHeading>
              <ImagePicker
                asParent={false}
                image={card[CARDS_PROP.IMAGE_SECOND]}
                hideCurrentPage={false}
                open={imagePickerOpen}
                setOpen={setImagePickerOpen}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.IMAGE_SECOND,
                    value,
                  })
                }
                imageReposition={true}
                ImageRepositionComponent={ImageFocalPoint}
                tabs={["Unsplash", "From page", "Link", "Upload"]}
                sidebarContainerStyles={{}}
                thumbnailContainerStyles={{}}
                libraryImages={[]}
                iconLibraryImages={[]}
                hideInfo={true}
              />
            </SettingsOptionContainer>
          )}
        </>
      )}

      {config.type === "illustration" && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("illustration")}</SettingsOptionHeading>
          <OptionContainer>
            <OptionContent>
              <IllustrationPicker
                illustration={card[CARDS_PROP.IMAGE]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.IMAGE,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel alignToTop>{t("link-to")}</OptionLabel>
          <OptionContent>
            <LinkPicker
              value={card[CARDS_PROP.LINK]}
              onChange={(value) =>
                updateCardData({
                  id: card.id,
                  key: CARDS_PROP.LINK,
                  value,
                })
              }
            />
          </OptionContent>
        </OptionContainer>
        {card[CARDS_PROP.LINK]?.source !== "email" && (
          <OptionContainer>
            <OptionLabel>{t("new-tab")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={card[CARDS_PROP.NEW_TAB] === "true"}
                onClick={(isOn) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.NEW_TAB,
                    value: isOn ? "true" : "false",
                  })
                }
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("content")}</SettingsOptionHeading>
        {config.type === "emoji" && (
          <OptionContainer>
            <OptionLabel>{t("emoji")}</OptionLabel>
            <OptionContent>
              <IconPicker
                defaultTab={t(isEmoji ? "emojis" : "icons")}
                tabs={ICON_OPTIONS[config.design]}
                icon={card[CARDS_PROP.ICON]}
                onChange={({ value }) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.ICON,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.ICON) && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <IconPicker
                defaultTab={"Icons"}
                tabs={ICON_OPTIONS[config.design] || ["Icons"]}
                icon={card[CARDS_PROP.ICON]}
                onChange={({ value }) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.ICON,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.type === CARD_TYPE.NUMBER && (
          <OptionContainer>
            <OptionLabel>{t("number")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder={t("add")}
                value={card[CARDS_PROP.NUMBER]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.NUMBER,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.TITLE) && (
          <OptionContainer>
            <OptionLabel>{t("title")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder={t("add")}
                value={card[CARDS_PROP.TITLE]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.TITLE,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.TEXT) && config.type !== "text" && (
          <OptionContainer>
            <OptionLabel>{t("subtext")}</OptionLabel>
            <OptionContent>
              <TextArea
                placeholder={t("add")}
                value={card[CARDS_PROP.TEXT]}
                onChange={({ target: { value } }) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.TEXT,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.TEXT) && config.type === "text" && (
          <OptionContainer direction="column">
            <OptionLabel>{t("subtext")}</OptionLabel>
            <TextEditorContainer>
              <TextEditor
                textColor={theme.text.input}
                linkBackgroundColor={theme.bg.primary}
                linkButtonsColor={theme.bg.primary}
                linkIconColor={theme.border.black50}
                linkActionBackgroundColor={theme.bg.primary}
                value={card ? card[CARDS_PROP.TEXT] : ""}
                onChangeComplete={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.TEXT,
                    value,
                  })
                }
              />
            </TextEditorContainer>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.LINK_TEXT) && (
          <OptionContainer>
            <OptionLabel>{t("link-text")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder={t("add")}
                value={card[CARDS_PROP.LINK_TEXT]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.LINK_TEXT,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {config.layout.includes(CARDS_PROP.LABEL) && (
          <OptionContainer>
            <OptionLabel>{t("label")}</OptionLabel>
            <OptionContent>
              <Text
                placeholder={t("add")}
                value={card[CARDS_PROP.LABEL]}
                onChange={(value) =>
                  updateCardData({
                    id: card.id,
                    key: CARDS_PROP.LABEL,
                    value,
                  })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>

      {(optionConditions.color || optionConditions.background) && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
          {optionConditions.background && (
            <OptionContainer>
              <OptionLabel alignToTop>{t("background")}</OptionLabel>
              <OptionContent>
                <ColorPicker
                  value={
                    card[CARDS_PROP.BACKGROUND_COLORS] ||
                    STANDARD_BACKGROUND_COLORS
                  }
                  onChange={(value) =>
                    updateCardData({
                      id: card.id,
                      key: CARDS_PROP.BACKGROUND_COLORS,
                      value,
                    })
                  }
                />
              </OptionContent>
            </OptionContainer>
          )}
          {optionConditions.color && (
            <OptionContainer>
              <OptionLabel alignToTop>{t("colors")}</OptionLabel>
              <OptionContent>
                <ColorPicker
                  hideColorTiles={
                    HIDE_COLOR_TILES[config.type]?.(config) || false
                  }
                  value={card[CARDS_PROP.COLORS]}
                  onChange={(value) =>
                    updateCardData({
                      id: card.id,
                      key: CARDS_PROP.COLORS,
                      value,
                    })
                  }
                />
              </OptionContent>
            </OptionContainer>
          )}
        </SettingsOptionContainer>
      )}

      {hasStyleguidePermission && (
        <SettingsOptionContainer style={{ border: "none", marginTop: "auto" }}>
          <OptionContainer>
            <AddToLibraryButton
              onClick={(action) =>
                handleCardLibraryRequest(LIBRARY_SERVICE_ACTION[action])
              }
              isSaved={!!selectedLibraryCard}
              isLoading={isRequestingToLibrary}
              hasChanges={
                selectedLibraryCard
                  ? !isEqual(
                      omit(selectedLibraryCard.cardData, ["color"]),
                      omit(card, ["color"])
                    ) || !isEqual(selectedLibraryCard.config, cardData.config)
                  : false
              }
              macroName="card"
            />
          </OptionContainer>
        </SettingsOptionContainer>
      )}
    </>
  );
};

export default CardOptions;
